import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import AdminSidebar from '../../components/sidebar/AdminSidebar';
import { adminRatesApi } from '../../services/api';
import { useNotifications } from '../../hooks/useNotifications';
import './BankAccountPage.css';

interface ExchangeRates {
  usdToNgnRate: number;
  ngnToUsdRate: number;
  lastUpdatedBy?: any;
  updatedAt?: string;
  notes?: string;
}

const ExchangeRatesPage: React.FC = () => {
  const notifications = useNotifications();

  const [exchangeRates, setExchangeRates] = useState<ExchangeRates>({
    usdToNgnRate: 1500,
    ngnToUsdRate: 0.0006667,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rateNotes, setRateNotes] = useState('');
  const [tempRates, setTempRates] = useState<ExchangeRates | null>(null);

  useEffect(() => {
    const loadRates = async () => {
      try {
        setLoading(true);
        const data = await adminRatesApi.getRates();
        setExchangeRates(data);
        setRateNotes(data.notes || '');
      } catch (error) {
        console.error('Failed to load exchange rates:', error);
      } finally {
        setLoading(false);
      }
    };

    loadRates();
  }, []);

  const handleRateChange = (field: 'usdToNgnRate' | 'ngnToUsdRate', value: number) => {
    if (!tempRates) {
      setTempRates(exchangeRates);
    }

    const updated = {
      ...(tempRates || exchangeRates),
      [field]: value,
    };

    if (field === 'usdToNgnRate' && value > 0) {
      updated.ngnToUsdRate = Number((1 / value).toFixed(10));
    } else if (field === 'ngnToUsdRate' && value > 0) {
      updated.usdToNgnRate = Number((1 / value).toFixed(10));
    }

    setTempRates(updated);
  };

  const handleSave = async () => {
    const rates = tempRates || exchangeRates;

    if (rates.usdToNgnRate <= 0 || rates.ngnToUsdRate <= 0) {
      notifications.error('Exchange rates must be positive values');
      return;
    }

    try {
      setIsSaving(true);
      await adminRatesApi.updateRates({
        usdToNgnRate: rates.usdToNgnRate,
        ngnToUsdRate: rates.ngnToUsdRate,
        notes: rateNotes,
      });

      setExchangeRates(rates);
      setTempRates(null);
      setIsEditing(false);
      notifications.success('Exchange rates updated successfully');
    } catch (error) {
      console.error('Error saving exchange rates:', error);
      notifications.error('Failed to save exchange rates. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setTempRates(null);
    setIsEditing(false);
  };

  return (
    <DashboardLayout sidebar={<AdminSidebar />}>
      <div className="bank-account-page">
        <div className="page-header">
          <h2>Exchange Rates Management</h2>
          <p>Control the USD to NGN exchange rates used across the platform</p>
        </div>

        <div className="bank-account-container">
          <div className="account-header">
            <h3>Exchange Rates</h3>
            {!isEditing && (
              <button onClick={() => setIsEditing(true)} className="edit-btn">
                Edit Rates
              </button>
            )}
          </div>

          {loading ? (
            <div className="loading">Loading exchange rates...</div>
          ) : (
            <>
              <div className="account-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="usdToNgn">USD to NGN Rate</label>
                    <input
                      type="number"
                      id="usdToNgn"
                      value={tempRates?.usdToNgnRate ?? exchangeRates.usdToNgnRate}
                      onChange={(event) => handleRateChange('usdToNgnRate', Number(event.target.value))}
                      disabled={!isEditing}
                      placeholder="e.g. 1500"
                      step="0.01"
                      min="0"
                    />
                    <small>Amount in NGN for 1 USD</small>
                  </div>
                  <div className="form-group">
                    <label htmlFor="ngnToUsd">NGN to USD Rate</label>
                    <input
                      type="number"
                      id="ngnToUsd"
                      value={tempRates?.ngnToUsdRate ?? exchangeRates.ngnToUsdRate}
                      onChange={(event) => handleRateChange('ngnToUsdRate', Number(event.target.value))}
                      disabled={!isEditing}
                      placeholder="e.g. 0.0006667"
                      step="0.0000001"
                      min="0"
                    />
                    <small>Amount in USD for 1 NGN (auto-calculated)</small>
                  </div>
                </div>

                {isEditing && (
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="rateNotes">Notes (Optional)</label>
                      <textarea
                        id="rateNotes"
                        value={rateNotes}
                        onChange={(event) => setRateNotes(event.target.value)}
                        placeholder="e.g. Updated based on treasury review."
                        rows={3}
                      />
                    </div>
                  </div>
                )}

                {isEditing && (
                  <div className="form-actions">
                    <button
                      onClick={handleCancel}
                      className="cancel-btn"
                      disabled={isSaving}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      className="save-btn"
                      disabled={isSaving}
                    >
                      {isSaving ? 'Saving...' : 'Save Exchange Rates'}
                    </button>
                  </div>
                )}
              </div>

              <div className="account-info">
                <h4>Current Exchange Rates:</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">USD to NGN:</span>
                    <span className="info-value">1 USD = NGN {exchangeRates.usdToNgnRate.toFixed(2)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">NGN to USD:</span>
                    <span className="info-value">1 NGN = ${exchangeRates.ngnToUsdRate.toFixed(8)}</span>
                  </div>
                  {exchangeRates.updatedAt && (
                    <div className="info-item">
                      <span className="info-label">Last Updated:</span>
                      <span className="info-value">{new Date(exchangeRates.updatedAt).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="security-note">
                <h4>Important Information</h4>
                <p>
                  These rates are used for all user payments and conversions.
                  <br />
                  Changes apply immediately to all users.
                  <br />
                  All calculations use only these admin-defined rates.
                  <br />
                  Rate changes are logged with audit trail metadata.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ExchangeRatesPage;
